import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './PainelAdministrativo.css';
import './ConferenciaHospital.css';

// Conferência do relatório do hospital (pronto-socorro) contra os atendimentos do convênio PAM.
// O cruzamento é feito no backend: routes_organized/financeiro/financeiro-conferencia-hospital.js

const STATUS = {
  OK: { label: 'Confere', cor: '#1baf7a', desc: 'Lançado nos dois sistemas com o mesmo valor' },
  VALOR_DIFERENTE: { label: 'Valor diferente', cor: '#e8a33a', desc: 'Lançado nos dois, mas com valor diferente' },
  SO_LAB: { label: 'Falta no hospital', cor: '#e34948', desc: 'Fizemos o exame e o hospital não lançou' },
  SO_HOSPITAL: { label: 'Falta no laboratório', cor: '#8a5cd6', desc: 'O hospital lançou e não está no nosso sistema' },
  SEM_CODIGO: { label: 'Sem código SIGTAP', cor: '#5b6472', desc: 'Exame do laboratório sem código SIGTAP na tabela do convênio' },
  FORA_PERIODO: { label: 'Fora do período', cor: '#8a93a2', desc: 'Lançado pelo hospital com data fora do período filtrado' },
};
const DIVERGENCIAS = ['SO_LAB', 'SO_HOSPITAL', 'VALOR_DIFERENTE', 'SEM_CODIGO', 'FORA_PERIODO'];

const moeda = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const numero = (v) => Number(v || 0).toLocaleString('pt-BR');
const dataBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function mesRelativo(delta) {
  const h = new Date();
  const ini = new Date(h.getFullYear(), h.getMonth() + delta, 1);
  const fim = new Date(h.getFullYear(), h.getMonth() + delta + 1, 0);
  return { inicio: iso(ini), fim: iso(fim) };
}

/** "09/2026" → período do mês */
function periodoDaCompetencia(comp) {
  const m = /^(\d{2})\/(\d{4})$/.exec(comp || '');
  if (!m) return null;
  const ini = new Date(Number(m[2]), Number(m[1]) - 1, 1);
  const fim = new Date(Number(m[2]), Number(m[1]), 0);
  return { inicio: iso(ini), fim: iso(fim) };
}

function exportarCSV(linhas, filtro) {
  const cab = ['Situação', 'Paciente (laboratório)', 'Paciente (hospital)', 'Documento', 'Data hospital', 'Data laboratório',
    'Unidade', 'Protocolo', 'Código SIGTAP', 'Exame', 'Exame lab', 'Valor hospital', 'Valor laboratório'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const num = (v) => (v == null ? '' : String(v).replace('.', ','));
  const corpo = linhas.map((l) => [STATUS[l.status]?.label, l.dataLab ? (l.paciente || l.nome) : '', l.nomeHospital, l.documento,
    dataBR(l.dataHospital), dataBR(l.dataLab), l.local, l.protocolo, l.codigo, l.descricao, l.exameLab,
    num(l.valorHospital), num(l.valorLab)].map(esc).join(';'));
  const blob = new Blob([`﻿${[cab.map(esc).join(';'), ...corpo].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `conferencia-hospital-${filtro.convenio}-${filtro.dataInicio}-a-${filtro.dataFim}.csv`;
  a.click();
}

const dataRef = (l) => l.dataHospital || l.dataLab;
const ordemItens = (a, b) => (a.paciente || a.nome || '').localeCompare(b.paciente || b.nome || '')
  || (dataRef(a) || '').localeCompare(dataRef(b) || '')
  || (a.codigo || '').localeCompare(b.codigo || '');

/** Soma as linhas por chave (dia ou paciente) para os níveis do drill-down. */
function agrupar(linhas, chaveFn) {
  const m = new Map();
  for (const l of linhas) {
    const k = chaveFn(l);
    if (!m.has(k)) {
      m.set(k, { chave: k, nome: l.paciente || l.nome, itensHosp: 0, itensLab: 0, valorHosp: 0, valorLab: 0,
        divergencias: 0, porStatus: {}, _pac: new Set(), _prot: new Set() });
    }
    const g = m.get(k);
    if (l.status !== 'FORA_PERIODO' && l.valorHospital != null) { g.itensHosp++; g.valorHosp += l.valorHospital; }
    if (l.valorLab != null) { g.itensLab++; g.valorLab += l.valorLab; }
    if (DIVERGENCIAS.includes(l.status)) g.divergencias++;
    g.porStatus[l.status] = (g.porStatus[l.status] || 0) + 1;
    g._pac.add(l.chave);
    if (l.protocolo) g._prot.add(`${l.local}/${l.protocolo}`);
  }
  return [...m.values()].map(({ _pac, _prot, ...g }) => ({
    ...g, pacientes: _pac.size, protocolos: [..._prot].sort(),
    valorHosp: Math.round(g.valorHosp * 100) / 100, valorLab: Math.round(g.valorLab * 100) / 100,
  }));
}

function Badge({ status, qtd }) {
  const st = STATUS[status];
  return (
    <span className="ch-badge" style={{ background: `${st.cor}1a`, color: st.cor }} title={st.desc}>
      {st.label}{qtd != null ? ` ${qtd}` : ''}
    </span>
  );
}

function Vazio({ aba }) {
  return (
    <div className="pa-vazio">
      {aba === 'DIVERGENCIAS' ? 'Nenhuma divergência: tudo o que foi lançado confere nos dois sistemas.' : 'Nada para mostrar.'}
    </div>
  );
}

function TabelaGrupos({ titulo, grupos, rotulo, colunaExtra, extra, onAbrir }) {
  return (
    <div className="pa-card">
      <div className="pa-tabela-scroll">
        <table className="pa-tabela ch-tabela">
          <thead>
            <tr>
              <th>{titulo}</th><th>{colunaExtra}</th>
              <th className="num">Itens hosp.</th><th className="num">Itens lab.</th>
              <th className="num">Valor hosp.</th><th className="num">Valor lab.</th><th className="num">Diferença</th>
              <th>Situação</th><th />
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => {
              const dif = Math.round((g.valorHosp - g.valorLab) * 100) / 100;
              return (
                <tr key={g.chave} className={`clicavel ${g.divergencias ? 'ch-tem-diverg' : ''}`} onClick={() => onAbrir(g)}>
                  <td><b>{rotulo(g)}</b></td>
                  <td className="ch-sub-cel">{extra(g)}</td>
                  <td className="num">{numero(g.itensHosp)}</td>
                  <td className="num">{numero(g.itensLab)}</td>
                  <td className="num">{moeda(g.valorHosp)}</td>
                  <td className="num">{moeda(g.valorLab)}</td>
                  <td className={`num ${dif ? 'ch-dif' : ''}`}>{dif ? moeda(dif) : '—'}</td>
                  <td>
                    {g.divergencias
                      ? DIVERGENCIAS.filter((k) => g.porStatus[k]).map((k) => <Badge key={k} status={k} qtd={g.porStatus[k]} />)
                      : <Badge status="OK" />}
                  </td>
                  <td className="ch-abrir">›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResumoPaciente({ itens }) {
  if (!itens.length) return null;
  const [g] = agrupar(itens, () => 'p');
  const nomesHosp = [...new Set(itens.map((l) => l.nomeHospital).filter(Boolean))];
  const docs = [...new Set(itens.map((l) => l.documento).filter(Boolean))];
  const diasHosp = [...new Set(itens.map((l) => l.dataHospital).filter(Boolean))].sort().map(dataBR);
  const diasLab = [...new Set(itens.map((l) => l.dataLab).filter(Boolean))].sort().map(dataBR);
  return (
    <div className="ch-info ch-resumo-pac">
      <strong>{g.nome}</strong>
      {nomesHosp.some((n) => n !== g.nome) && <div>No hospital: {nomesHosp.join(' / ')}</div>}
      <div>
        Hospital: <b>{g.itensHosp}</b> itens, {moeda(g.valorHosp)}{diasHosp.length ? ` · ${diasHosp.join(', ')}` : ''}
        {docs.length ? ` · doc. ${docs.join(', ')}` : ''}
      </div>
      <div>
        Laboratório: <b>{g.itensLab}</b> itens, {moeda(g.valorLab)}{diasLab.length ? ` · ${diasLab.join(', ')}` : ''}
        {g.protocolos.length ? ` · protocolos ${g.protocolos.join(', ')}` : ''}
      </div>
    </div>
  );
}

function TabelaItens({ linhas, onPaciente }) {
  return (
    <div className="pa-card">
      <div className="pa-tabela-scroll">
        <table className="pa-tabela ch-tabela">
          <thead>
            <tr>
              <th>Situação</th><th>Paciente</th><th>Data hosp.</th><th>Data lab.</th><th>Protocolo</th>
              <th>SIGTAP</th><th>Exame</th><th className="num">Valor hosp.</th><th className="num">Valor lab.</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i} className={DIVERGENCIAS.includes(l.status) ? 'ch-tem-diverg' : ''}>
                <td><Badge status={l.status} /></td>
                <td>
                  {onPaciente
                    ? <button className="ch-link" onClick={() => onPaciente(l)} title="Abrir o paciente">{l.paciente || l.nome}</button>
                    : (l.paciente || l.nome)}
                  {l.nomeHospital && l.nomeHospital !== (l.paciente || l.nome) && <div className="ch-sub">no hospital: {l.nomeHospital}</div>}
                  {['SO_LAB', 'SO_HOSPITAL', 'SEM_CODIGO'].includes(l.status) && l.pacienteNoOutroLado === false && (
                    <div className="ch-sub ch-atencao">paciente não encontrado no outro sistema</div>
                  )}
                </td>
                <td>{dataBR(l.dataHospital)}</td>
                <td>{dataBR(l.dataLab)}</td>
                <td>{l.protocolo ? `${l.local}/${l.protocolo}` : '—'}</td>
                <td className="ch-mono">{l.codigo || '—'}</td>
                <td>
                  {l.descricao}
                  {l.exameLab && <div className="ch-sub">{l.exameLab}</div>}
                </td>
                <td className="num">{moeda(l.valorHospital)}</td>
                <td className="num">{moeda(l.valorLab)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Texto para mandar ao hospital: só as divergências, item a item, sem totais nem dados
 * internos (protocolo, convênio interno). Nome e documento como estão no relatório deles.
 */
function textoParaHospital(dados) {
  const { linhas, filtro, cabecalho } = dados;
  const descSigtap = new Map();
  const nomeHosp = new Map();
  const docHosp = new Map();
  for (const l of linhas) {
    if (l.valorHospital != null && l.codigo) descSigtap.set(l.codigo, l.descricao);
    if (l.nomeHospital) nomeHosp.set(l.chave, l.nomeHospital);
    if (l.documento) docHosp.set(l.chave, l.documento);
  }
  const brl = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const exame = (l) => `${descSigtap.get(l.codigo) || l.descricao}${l.codigo ? ` (${l.codigo})` : ''}`;

  const secao = (titulo, status, linhaItem) => {
    const itens = linhas.filter((l) => l.status === status);
    if (!itens.length) return null;
    const grupos = new Map();
    for (const l of itens) {
      const data = l.status === 'SO_LAB' ? l.dataLab : (l.dataHospital || l.dataLab);
      const k = `${l.chave}|${data}`;
      if (!grupos.has(k)) grupos.set(k, { l, data, itens: [] });
      grupos.get(k).itens.push(l);
    }
    const blocos = [...grupos.values()]
      .sort((a, b) => a.data.localeCompare(b.data) || a.l.chave.localeCompare(b.l.chave))
      .map(({ l, data, itens: its }) => {
        const nome = nomeHosp.get(l.chave) || l.paciente || l.nome;
        const doc = docHosp.get(l.chave);
        return [`• ${nome} – ${dataBR(data)}${doc ? ` (doc. ${doc})` : ''}`, ...its.map((x) => `   - ${linhaItem(x)}`)].join('\n');
      });
    return `*${titulo}*\n\n${blocos.join('\n\n')}`;
  };

  const partes = [
    secao('Exames realizados pelo laboratório e não lançados no relatório', 'SO_LAB',
      (x) => `${exame(x)} – ${brl(x.valorLab)}`),
    secao('Exames lançados no relatório que não localizamos no laboratório', 'SO_HOSPITAL',
      (x) => `${exame(x)} – ${brl(x.valorHospital)}`),
    secao('Exames com valor diferente', 'VALOR_DIFERENTE',
      (x) => `${exame(x)} – no relatório ${brl(x.valorHospital)}, na tabela ${brl(x.valorLab)}`),
  ].filter(Boolean);

  const comp = cabecalho.competencia ? ` – competência ${cabecalho.competencia}` : '';
  const cab = `Conferência dos exames do pronto-socorro${comp} (atendimentos de ${dataBR(filtro.dataInicio)} a ${dataBR(filtro.dataFim)}).`;
  if (!partes.length) return `${cab}\n\nTodos os exames lançados no relatório conferem com os realizados pelo laboratório.`;
  return `${cab}\n\nConferimos o relatório com os nossos registros e encontramos as divergências abaixo:\n\n${partes.join('\n\n')}`;
}

function ModalTexto({ texto, onFechar }) {
  const [valor, setValor] = useState(texto);
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
    } catch {
      const t = document.getElementById('ch-texto-hospital');
      t.select(); document.execCommand('copy');
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };
  return (
    <div className="pa-overlay" onClick={onFechar}>
      <div className="pa-modal ch-modal-texto" onClick={(e) => e.stopPropagation()}>
        <div className="pa-modal-header">
          <div>
            <h2>Texto para o hospital</h2>
            <small>Só as divergências, item a item. Pode editar antes de copiar.</small>
          </div>
          <button className="pa-modal-fechar" onClick={onFechar} aria-label="Fechar">&times;</button>
        </div>
        <div className="pa-modal-body">
          <textarea id="ch-texto-hospital" className="ch-texto" value={valor} onChange={(e) => setValor(e.target.value)} />
          <div className="ch-texto-acoes">
            <button className="pa-btn" onClick={copiar}>{copiado ? '✓ Copiado' : 'Copiar texto'}</button>
            <button className="pa-btn pa-btn-secundario" onClick={onFechar}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConferenciaHospital() {
  const anterior = mesRelativo(-1);
  const [dataInicio, setDataInicio] = useState(anterior.inicio);
  const [dataFim, setDataFim] = useState(anterior.fim);
  const [convenio, setConvenio] = useState('PAM');
  const [convenios, setConvenios] = useState(['PAM']);
  const [arquivo, setArquivo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState(null);
  const [aba, setAba] = useState('DIVERGENCIAS');
  const [busca, setBusca] = useState('');
  // drill-down: dias → pacientes do dia → exames do paciente
  const [visao, setVisao] = useState('drill');
  const [dia, setDia] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [mostrarTexto, setMostrarTexto] = useState(false);

  // Esc = drill out
  useEffect(() => {
    const tecla = (e) => {
      if (e.key !== 'Escape' || visao !== 'drill') return;
      if (paciente) setPaciente(null); else if (dia) setDia(null);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [visao, dia, paciente]);

  useEffect(() => {
    axios.get(`${API_URL}/financeiro/conferencia-hospital/convenios`)
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setConvenios(r.data); })
      .catch(() => {});
  }, []);

  const atalhos = [
    { label: 'Mês anterior', ...mesRelativo(-1) },
    { label: 'Mês atual', ...mesRelativo(0) },
    { label: 'Há 2 meses', ...mesRelativo(-2) },
  ];

  async function conferir() {
    if (!arquivo) { setErro('Selecione o relatório do hospital (PDF).'); return; }
    setErro(''); setCarregando(true); setDados(null);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('dataInicio', dataInicio);
      fd.append('dataFim', dataFim);
      fd.append('convenio', convenio);
      const r = await axios.post(`${API_URL}/financeiro/conferencia-hospital/upload`, fd, { timeout: 180000 });
      setDados(r.data);
      setDia(null); setPaciente(null);
      setAba(DIVERGENCIAS.some((s) => r.data.resumo[s] > 0) ? 'DIVERGENCIAS' : 'TODOS');
    } catch (e) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setCarregando(false);
    }
  }

  const passaStatus = (l) => (aba === 'TODOS' ? true : aba === 'DIVERGENCIAS' ? DIVERGENCIAS.includes(l.status) : l.status === aba);

  const visiveis = useMemo(() => {
    if (!dados) return [];
    const q = busca.trim().toUpperCase();
    return dados.linhas
      .filter(passaStatus)
      .filter((l) => !q || `${l.paciente || l.nome} ${l.nomeHospital || ''} ${l.protocolo || ''} ${l.descricao}`.toUpperCase().includes(q))
      .sort(ordemItens);
  }, [dados, aba, busca]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dias / pacientes que têm ao menos uma linha no filtro (status + busca); os números
  // de cada grupo, porém, contam TODAS as linhas dele — senão a soma de um paciente "some".
  const chavesVisiveis = useMemo(() => new Set(visiveis.map((l) => `${dataRef(l)}|${l.chave}`)), [visiveis]);

  const dias = useMemo(() => {
    if (!dados) return [];
    const diasVisiveis = new Set(visiveis.map(dataRef));
    return agrupar(dados.linhas.filter((l) => diasVisiveis.has(dataRef(l))), dataRef)
      .sort((a, b) => a.chave.localeCompare(b.chave));
  }, [dados, visiveis]);

  const pacientesDoDia = useMemo(() => {
    if (!dados || !dia) return [];
    return agrupar(dados.linhas.filter((l) => dataRef(l) === dia && chavesVisiveis.has(`${dia}|${l.chave}`)), (l) => l.chave)
      .sort((a, b) => (b.divergencias - a.divergencias) || a.nome.localeCompare(b.nome));
  }, [dados, dia, chavesVisiveis]);

  // No último nível mostra o paciente no período inteiro: o mesmo atendimento pode ter
  // data diferente em cada sistema (ex.: hospital lança tudo no dia 24, o lab tem 24 e 25).
  const itensDoPaciente = useMemo(() => {
    if (!dados || !paciente) return [];
    return dados.linhas.filter((l) => l.chave === paciente).sort(ordemItens);
  }, [dados, paciente]);

  const abrirPaciente = (l) => { setVisao('drill'); setDia(dataRef(l)); setPaciente(l.chave); };

  const competencia = dados?.cabecalho?.competencia;
  const periodoComp = periodoDaCompetencia(competencia);
  const periodoDifere = periodoComp && dados && (periodoComp.inicio !== dados.filtro.dataInicio || periodoComp.fim !== dados.filtro.dataFim);
  const totalDiverg = dados ? DIVERGENCIAS.reduce((s, k) => s + (dados.resumo[k] || 0), 0) : 0;

  return (
    <div className="pa-root ch-root">
      <div className="pa-header">
        <div>
          <h1>🏥 Conferência Hospital × Laboratório</h1>
          <p>Compara o relatório de exames lançados pelo hospital (pronto-socorro) com os atendimentos do convênio no nosso sistema.</p>
        </div>
        <span className="pa-fonte" title="Exame casa por código SIGTAP; paciente por nome (o relatório corta o nome); data com até 1 dia de diferença.">
          Casamento: SIGTAP + nome + data
        </span>
      </div>

      <div className="pa-filtros">
        <div className="pa-filtros-linha">
          <div className="pa-campo">
            <label>Atendimento de</label>
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="pa-campo">
            <label>Até</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="pa-campo">
            <label>Convênio</label>
            <select value={convenio} onChange={(e) => setConvenio(e.target.value)}>
              {convenios.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="pa-campo" style={{ flex: '1 1 260px' }}>
            <label>Relatório do hospital (PDF)</label>
            <input type="file" accept=".pdf,.txt" onChange={(e) => setArquivo(e.target.files?.[0] || null)} />
          </div>
          <div className="pa-campo">
            <label>&nbsp;</label>
            <button className="pa-btn" onClick={conferir} disabled={carregando}>
              {carregando ? 'Conferindo...' : 'Conferir'}
            </button>
          </div>
        </div>
        <div className="pa-atalhos" style={{ marginTop: 10 }}>
          {atalhos.map((a) => (
            <button key={a.label}
              className={`pa-atalho ${dataInicio === a.inicio && dataFim === a.fim ? 'ativo' : ''}`}
              onClick={() => { setDataInicio(a.inicio); setDataFim(a.fim); }}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {erro && <div className="pa-erro">{erro}</div>}
      {carregando && <div className="pa-loading"><div className="pa-spinner" />Lendo o relatório e cruzando com o sistema...</div>}

      {dados && (
        <>
          <div className="ch-info">
            <div><strong>{dados.cabecalho.hospital || 'Hospital'}</strong></div>
            <div>
              Competência <b>{competencia || '—'}</b> · emitido em {dados.cabecalho.emitidoEm || '—'} · {dados.cabecalho.arquivo}
            </div>
            <div>
              Período conferido <b>{dataBR(dados.filtro.dataInicio)} a {dataBR(dados.filtro.dataFim)}</b> · convênio <b>{dados.filtro.convenio}</b>
            </div>
            {dados.cabecalho.leituraConfere
              ? <div className="ch-ok">✓ Leitura do PDF confere com o total impresso no relatório ({moeda(dados.cabecalho.totalImpresso)}).</div>
              : <div className="ch-alerta">⚠ A soma das linhas lidas ({moeda(dados.cabecalho.totalLido)}) não bate com o total impresso ({moeda(dados.cabecalho.totalImpresso)}). Alguma linha do PDF não foi reconhecida.</div>}
            {periodoDifere && (
              <div className="ch-alerta">
                ⚠ O relatório é da competência {competencia}, mas o período filtrado é outro.{' '}
                <button className="ch-link" onClick={() => { setDataInicio(periodoComp.inicio); setDataFim(periodoComp.fim); }}>
                  Usar {dataBR(periodoComp.inicio)} a {dataBR(periodoComp.fim)}
                </button> e conferir de novo.
              </div>
            )}
          </div>

          <div className="pa-kpis">
            <div className="pa-kpi">
              <div className="pa-kpi-label">Itens no hospital</div>
              <div className="pa-kpi-valor">{numero(dados.itensHospital)}</div>
              <div className="pa-kpi-nota">{moeda(dados.valores.hospital)}</div>
            </div>
            <div className="pa-kpi">
              <div className="pa-kpi-label">Itens no laboratório</div>
              <div className="pa-kpi-valor">{numero(dados.itensLab)}</div>
              <div className="pa-kpi-nota">{moeda(dados.valores.lab)}</div>
            </div>
            {['OK', 'SO_LAB', 'SO_HOSPITAL', 'VALOR_DIFERENTE'].map((k) => (
              <div key={k} className="pa-kpi" style={{ borderLeftColor: STATUS[k].cor }} title={STATUS[k].desc}>
                <div className="pa-kpi-label">{STATUS[k].label}</div>
                <div className="pa-kpi-valor">{numero(dados.resumo[k])}</div>
                <div className="pa-kpi-nota">
                  {k === 'SO_LAB' && moeda(dados.valores.soLab)}
                  {k === 'SO_HOSPITAL' && moeda(dados.valores.soHospital)}
                  {(k === 'OK' || k === 'VALOR_DIFERENTE') && STATUS[k].desc}
                </div>
              </div>
            ))}
          </div>

          <div className="ch-barra">
            <div className="ch-abas">
              <button className={`pa-chip ${aba === 'DIVERGENCIAS' ? 'ativo' : ''}`} onClick={() => setAba('DIVERGENCIAS')}>
                Divergências ({numero(totalDiverg)})
              </button>
              {Object.entries(STATUS).filter(([k]) => dados.resumo[k] > 0).map(([k, st]) => (
                <button key={k} className={`pa-chip ${aba === k ? 'ativo' : ''}`} onClick={() => setAba(k)} title={st.desc}>
                  <i className="pa-ponto" style={{ color: st.cor, opacity: 1 }} />{st.label} ({numero(dados.resumo[k])})
                </button>
              ))}
              <button className={`pa-chip ${aba === 'TODOS' ? 'ativo' : ''}`} onClick={() => setAba('TODOS')}>
                Todos ({numero(dados.linhas.length)})
              </button>
            </div>
            <div className="ch-acoes">
              <div className="ch-alternar">
                <button className={visao === 'drill' ? 'ativo' : ''} onClick={() => setVisao('drill')}>Por dia / paciente</button>
                <button className={visao === 'lista' ? 'ativo' : ''} onClick={() => setVisao('lista')}>Lista de itens</button>
              </div>
              <input className="ch-busca" placeholder="Buscar paciente, protocolo ou exame" value={busca} onChange={(e) => setBusca(e.target.value)} />
              <button className="pa-btn" onClick={() => setMostrarTexto(true)} title="Texto pronto com as divergências para copiar e enviar ao hospital">
                Texto para o hospital
              </button>
              <button className="pa-btn pa-btn-secundario"
                onClick={() => exportarCSV(visao === 'drill' && paciente ? itensDoPaciente : visiveis, dados.filtro)}>
                Exportar CSV
              </button>
            </div>
          </div>

          {visao === 'drill' && (
            <div className="ch-trilha">
              {(dia || paciente) && (
                <button className="ch-voltar" onClick={() => (paciente ? setPaciente(null) : setDia(null))} title="Voltar um nível (Esc)">← Voltar</button>
              )}
              <button className={`ch-migalha ${!dia ? 'atual' : ''}`} onClick={() => { setDia(null); setPaciente(null); }}>
                Todos os dias
              </button>
              {dia && (
                <>
                  <span className="ch-sep">›</span>
                  <button className={`ch-migalha ${!paciente ? 'atual' : ''}`} onClick={() => setPaciente(null)}>{dataBR(dia)}</button>
                </>
              )}
              {paciente && (
                <>
                  <span className="ch-sep">›</span>
                  <span className="ch-migalha atual">{itensDoPaciente[0]?.paciente || paciente}</span>
                </>
              )}
            </div>
          )}

          {visao === 'drill' && !dia && (
            dias.length === 0 ? <Vazio aba={aba} /> : (
              <TabelaGrupos
                titulo="Dia" grupos={dias} rotulo={(g) => dataBR(g.chave)} colunaExtra="Pacientes"
                extra={(g) => numero(g.pacientes)} onAbrir={(g) => setDia(g.chave)}
              />
            )
          )}

          {visao === 'drill' && dia && !paciente && (
            pacientesDoDia.length === 0 ? <Vazio aba={aba} /> : (
              <TabelaGrupos
                titulo="Paciente" grupos={pacientesDoDia} rotulo={(g) => g.nome} colunaExtra="Protocolos"
                extra={(g) => g.protocolos.join(', ') || '—'} onAbrir={(g) => setPaciente(g.chave)}
              />
            )
          )}

          {visao === 'drill' && paciente && (
            <>
              <ResumoPaciente itens={itensDoPaciente} />
              <TabelaItens linhas={itensDoPaciente} />
            </>
          )}

          {mostrarTexto && <ModalTexto texto={textoParaHospital(dados)} onFechar={() => setMostrarTexto(false)} />}

          {visao === 'lista' && (
            visiveis.length === 0 ? <Vazio aba={aba} /> : <TabelaItens linhas={visiveis} onPaciente={abrirPaciente} />
          )}
        </>
      )}
    </div>
  );
}
